import { useEffect, useState } from 'react'
import { Spin } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { getStockFinancedetail } from '@/api/stock'
import type { StockFinancialData } from '@/types/stockPool'

const reportTypeLabel: Record<string, string> = {
    annual: '年报', interim_h1: '中报(H1)', interim_h2: '中报(H2)',
    quarterly_q1: 'Q1', quarterly_q2: 'Q2', quarterly_q3: 'Q3', quarterly_q4: 'Q4',
    quarterly: '季报', research: '研报',
}
const reportTypeColor: Record<string, string> = {
    annual: '#4f8cff', interim_h1: '#22c55e', interim_h2: '#22c55e',
    quarterly_q1: '#eab308', quarterly_q2: '#eab308', quarterly_q3: '#eab308', quarterly_q4: '#eab308',
    quarterly: '#eab308', research: '#a78bfa',
}

function fmtBig(v: number | null | undefined) {
    if (v == null) return '—'
    if (Math.abs(v) >= 1e8) return (v / 1e8).toFixed(1) + '亿'
    if (Math.abs(v) >= 1e4) return (v / 1e4).toFixed(1) + '万'
    return v.toFixed(2)
}

function PctSpan({ v }: { v: number | null | undefined }) {
    if (v == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
    return <span className={v >= 0 ? 'pos' : 'neg'}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>
}

export default function Financials({ fallbackIndicators }: { fallbackIndicators?: any[] }) {
    const navigate = useNavigate()
    const { code } = useParams<{ code: string }>()
    const [items, setItems] = useState<StockFinancialData[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!code) return
        setLoading(true)
        getStockFinancedetail(decodeURIComponent(code))
            .then(res => {
                if (res.items.length > 0) {
                    setItems(res.items)
                } else if (fallbackIndicators) {
                    setItems(fallbackIndicators as StockFinancialData[])
                }
            })
            .catch(() => {
                if (fallbackIndicators) setItems(fallbackIndicators as StockFinancialData[])
            })
            .finally(() => setLoading(false))
    }, [code])

    const toReport = (id: number) =>
        navigate(`/stock-kb/${encodeURIComponent(code ?? '')}/report/${id}`)

    if (loading) return <div style={{ padding: '60px 0', textAlign: 'center' }}><Spin /></div>

    if (!items.length) {
        return <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-3)' }}>暂无财报数据</div>
    }

    return (
        <div className="skd__fin-list">
            <div className="skd__fin-list-header">
                <span className="skd__fin-list-title">历史财报</span>
                <span className="skd__fin-list-count">{items.length} 份</span>
            </div>
            <div className="skd__fin-list-table-wrap">
                <table className="skd__fin-list-table">
                    <thead>
                        <tr>
                            <th>报告期</th>
                            <th>类型</th>
                            <th>报告日期</th>
                            <th>营业收入</th>
                            <th>收入 YoY</th>
                            <th>归母净利润</th>
                            <th>利润 YoY</th>
                            <th>毛利率</th>
                            <th>PE(TTM)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(ind => {
                            const rt = (ind as any).report_type ?? ''
                            const typeLabel = reportTypeLabel[rt] ?? rt ?? '—'
                            const typeColor = reportTypeColor[rt] ?? '#64748b'
                            return (
                                <tr key={ind.id} onClick={() => toReport(ind.id)} className="skd__fin-list-row">
                                    <td className="skd__fin-list-period">{(ind as any).fiscal_year ?? '—'}</td>
                                    <td>
                                        <span className="skd__fin-list-type" style={{ background: `${typeColor}20`, color: typeColor }}>
                                            {typeLabel}
                                        </span>
                                    </td>
                                    <td className="skd__fin-list-date">{ind.report_date}</td>
                                    <td className="skd__fin-list-num">{fmtBig(ind.revenue)}</td>
                                    <td><PctSpan v={ind.revenue_yoy_pct} /></td>
                                    <td className="skd__fin-list-num">{fmtBig(ind.net_profit_parent)}</td>
                                    <td><PctSpan v={ind.adjusted_net_profit_yoy_pct} /></td>
                                    <td className="skd__fin-list-num">{ind.gross_margin_pct != null ? ind.gross_margin_pct.toFixed(1) + '%' : '—'}</td>
                                    <td className="skd__fin-list-num">{ind.pe_ttm && ind.pe_ttm > 0 ? ind.pe_ttm.toFixed(1) + 'x' : '—'}</td>
                                    <td><span className="skd__fin-list-btn">查看全部字段 →</span></td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
